import { useRef } from "react";
import { motion, useMotionValue, useSpring, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { scrollToId } from "../../lib/api";
import { SpinBadge } from "./SpinBadge";

const LINES = [
  { text: "We plant", cls: "" },
  { text: "big smiles", cls: "text-[var(--coral)]" },
  { text: "on your lawn.", cls: "" },
];
const ease = [0.22, 1, 0.36, 1];

const MaskedLines = () => (
  <h1 className="font-display font-bold leading-[0.92] tracking-tight text-[2.9rem] sm:text-6xl lg:text-7xl xl:text-[5.6rem]">
    {LINES.map((l, i) => (
      <span key={l.text} className="block overflow-hidden pb-1">
        <motion.span
          className={`block ${l.cls}`}
          initial={{ y: "110%", rotate: 3 }}
          animate={{ y: "0%", rotate: 0 }}
          transition={{ duration: 1, delay: 0.15 + i * 0.14, ease }}
        >
          {l.text}
        </motion.span>
      </span>
    ))}
  </h1>
);

const TiltPhoto = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 14 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 14 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };
  return (
    <div ref={ref} className="relative [perspective:1200px]" onMouseMove={onMove} onMouseLeave={reset}>
      <motion.div
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        initial={{ opacity: 0, scale: 0.9, rotate: -4 }}
        animate={{ opacity: 1, scale: 1, rotate: 2 }}
        transition={{ duration: 1.1, delay: 0.35, ease }}
        className="relative"
      >
        <div className="sticker rounded-[36px] overflow-hidden bg-[var(--sun)] aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5]">
          <motion.img
            data-testid="hero-image"
            src="/images/hero.jpg"
            alt="A front lawn decorated with a colorful HAPPY BIRTHDAY yard sign"
            style={{ y: imgY, scale: 1.25 }}
            className="w-full h-full object-cover object-[50%_60%]"
          />
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_50%_70%,transparent_45%,rgba(27,26,46,0.35))]" />
        </div>
        <div
          style={{ transform: "translateZ(60px)" }}
          className="sticker-sm absolute -left-4 sm:-left-8 bottom-10 bg-white rounded-2xl px-4 py-3 -rotate-6"
        >
          <p className="font-hand text-xl leading-none">planted by kids</p>
          <p className="text-xs font-semibold text-[var(--ink-soft)] mt-1">parents drive the van</p>
        </div>
        <div style={{ transform: "translateZ(90px)" }} className="absolute -right-3 -top-6 sm:-right-8 sm:-top-4">
          <SpinBadge />
        </div>
      </motion.div>
    </div>
  );
};

export const Hero = () => (
  <section id="top" data-testid="hero-section" className="relative pt-[112px] pb-16 lg:pb-24 overflow-hidden">
    <div className="absolute inset-0 dots-bg opacity-[0.08] pointer-events-none" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
      <div className="lg:col-span-7">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease }}
          className="font-hand text-2xl text-[var(--grass)] mb-4 -rotate-1"
        >
          psst... someone's having a big day
        </motion.p>
        <MaskedLines />
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7, ease }}
          className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-[var(--ink-soft)]"
        >
          Celebration yard signs for birthdays, grads, new babies and every win in between. Set up by local kids
          (with their parents' help) who are saving up, one lawn at a time.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85, ease }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <motion.button
            whileHover={{ y: -3, boxShadow: "9px 9px 0 #1B1A2E" }}
            whileTap={{ scale: 0.96 }}
            data-testid="hero-book-btn"
            onClick={() => scrollToId("book")}
            className="sticker rounded-full bg-[var(--coral)] text-white font-display font-semibold text-lg px-8 py-4 flex items-center gap-2"
          >
            Book your sign <ArrowDownRight size={20} />
          </motion.button>
          <motion.button
            whileHover={{ y: -3 }}
            whileTap={{ scale: 0.96 }}
            data-testid="hero-crew-btn"
            onClick={() => scrollToId("crew")}
            className="rounded-full font-display font-semibold text-lg px-6 py-4 underline decoration-[var(--sun)] decoration-[6px] underline-offset-4"
          >
            Meet the crew
          </motion.button>
        </motion.div>
      </div>
      <div className="lg:col-span-5 px-2 sm:px-10 lg:px-0">
        <TiltPhoto />
      </div>
    </div>
  </section>
);
